/* @flow */
/* eslint-env es6,browser */

import adconfig from './adconfig';
import containerHelper from './container-helper';
import { logDebug } from './utils/logger';
import { noop, isFunction, isNumber, intersect } from './utils/coreutils';
import {
  getBrowserExcludeList,
  isBrowserExcluded,
  percentageInViewPort
} from './utils/browserutil';

import pubsub from './utils/pubsub';
import visibilityhelper, {
  VISIBILITYCHANGE_EVENT
} from './utils/visibilityhelper';

/**
 * @description Implements adrotation functionality
 * @exports adrotation
 * @requires  module:browserutil
 * @requires  module:adconfig
 * @requires module:containerHelper
 * @requires module:logger
 */
class _adrotation {
  _doc: Document;

  _win: WindowProxy;

  _pageStart: number;

  _tidRotate: ?TimeoutID;

  _getAdsCB: (string, Array<string>, boolean, boolean) => mixed;

  _getAdsCBCxt: mixed;

  _config: {
    excludeList: Array<string>,
    positions: Array<string>,
    delay: number,
    durationEnd: number,
    onlyActivePage: boolean
  };

  _lastAdRotationTime: number;

  _defConfig: {
    onlyActivePage: boolean
  };

  constructor() {
    /**
     * @global
     * @property {Array<string>} [excludeList] Browsers to exclude
     * @property {Array<string>} positions Ad rotation positions
     * @property {number} delay Ad rotation delay
     * @property {number} durationEnd Ad rotation stop duration
     */
    this._config = {
      excludeList: [],
      positions: [],
      delay: 0,
      durationEnd: -1,
      onlyActivePage: false
    };
    this._defConfig = {
      onlyActivePage: false
    };
    this._pageStart = 0;
    this._tidRotate = undefined;
    this._getAdsCB = noop;
    this._getAdsCBCxt = undefined;
    this._lastAdRotationTime = 0;
    this._doc = document;
    this._win = window;
  }

  /**
   * Initialize the object
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} g - global param object
   * @returns {undefined}
   */
  /* eslint-disable-next-line no-unused-vars */
  init(w: WindowProxy, d: Document, g: globalParam) {
    this._doc = d;
    this._win = w;
    this._config.onlyActivePage = this._defConfig.onlyActivePage;

    visibilityhelper.init(w, d);
    // subscribe to event
    pubsub.on(VISIBILITYCHANGE_EVENT, isDocHidden => {
      // When page state changes from inactive to active trigger ad rotation
      if (this._config.onlyActivePage && !isDocHidden) {
        this._triggerAfterPageIsActive();
      }
    });
  }

  /**
   * updates config
   *
   * @external rotationConfig
   * @param {rotationConfig} o - Config for ad rotation
   * @returns {undefined}
   */
  updateConfig(o: rotationConfig) {
    this._config.delay = o.duration;
    this._config.durationEnd = o.durationEnd || -1;
    this._config.excludeList = getBrowserExcludeList(o.exclude);
    this._config.onlyActivePage =
      o.onlyActivePage || this._defConfig.onlyActivePage;

    const sPositions = o.positions || '';
    this._config.positions = sPositions.length > 0 ? sPositions.split(',') : [];
    // pass rotation list to container helper
    containerHelper.setRotationList(this.getPositions());
  }

  /**
   * initialize page start
   *
   * @returns {undefined}
   */
  setPageStart() {
    this._pageStart = Date.now();
  }

  /**
   * sets epoch when ads are loaded or rotated last
   *
   * @returns {undefined}
   */
  setLastRotationTime() {
    this._lastAdRotationTime = Date.now();
  }

  /**
   * @global
   * @typedef function
   * @callback getAdsCB
   * @param {string} page
   * @param {Array<string>} posList
   * @param {boolean} [bCache=true]
   * @param {boolean} [bRotate=false]
   */
  /**
   * setter for getAds callback used for ad rotation
   *
   * @param {getAdsCB} cb - callback function
   * @param {object} cxt - context
   * @returns {undefined}
   */
  setGetAdsCB(
    cb: (string, Array<string>, boolean, boolean) => mixed,
    cxt: mixed
  ) {
    this._getAdsCB = cb;
    this._getAdsCBCxt = cxt;
  }

  /**
   * get list of adrotation positions
   *
   * @returns {Array<string>} - Ad rotation position list
   * @private
   */
  _getAdRotationList(): Array<string> {
    // get only the ads for the current page;
    return intersect(
      adconfig.getPagePositions(undefined, undefined, true),
      this._config.positions
    );
  }

  /**
   * returns whether to rotate or not
   *
   * @returns {boolean} -Is next ad rotation required
   * @private
   */
  _shouldRotate(): boolean {
    if (this._config.durationEnd <= 0) {
      return false;
    }
    const bPassed =
      this._pageStart > 0 &&
      this._config.durationEnd > 0 &&
      Date.now() - this._pageStart > this._config.durationEnd * 1000;
    // we should get ad rotation timer value
    // current page shouldnt be in exclude list
    // current page has adrotation positions
    return (
      !bPassed &&
      this._config.delay > 0 &&
      !isBrowserExcluded(adconfig.getPageName(), this._config.excludeList) &&
      this._getAdRotationList().length > 0
    );
  }

  /**
   * takes care of ad rotation
   *
   * @returns {undefined}
   * @private
   */
  _rotate() {
    this._tidRotate = undefined;

    const isPageHidden = visibilityhelper.isDocHidden();

    // Prevent immediate triggered ad rotation as soon as page state changes from active to inactive
    if (this._config.onlyActivePage && isPageHidden) {
      logDebug('Skip rotation as page is hidden');
      return;
    }
    const list = this._getAdRotationList().filter(pos => {
      const { minPercentRotationViewport = 0, onlyActivePage = false } =
        adconfig.getPosSettings(pos);

      if (onlyActivePage && isPageHidden) {
        logDebug(`Skip rotation for "${pos}" as page is hidden`);
        return false;
      }

      if (minPercentRotationViewport === 0) {
        return true;
      }
      // Get active container div of the ad position
      const adContainerInfo: adInfo = containerHelper.getAdContainerInfo(
        pos,
        true
      );
      const inView = percentageInViewPort(this._doc, adContainerInfo.cID);
      const bRet = inView >= minPercentRotationViewport;
      if (!bRet) {
        logDebug(
          `Skip rotation for "${pos}" as it's ${inView} < ${minPercentRotationViewport}% in viewport`
        );
      }
      return bRet;
    });

    if (
      list.length > 0 &&
      this._getAdsCB !== null &&
      isFunction(this._getAdsCB)
    ) {
      logDebug(`Rotating ads ${list.join(',')}`);
      this._getAdsCB.call(
        this._getAdsCBCxt,
        adconfig.getPageName(),
        list,
        false,
        true
      );
    }
    // When the page has no rotatable ad positions OR the all existing rotatable positions in the page are out of viewport, trigger ad rotation after delay.
    else {
      this.trigger();
    }
  }

  /**
   * Calculated the delay since last ad roation and triggers ad rotation with appropiate delay
   *
   * @private
   * @returns {undefined}
   */
  _triggerAfterPageIsActive() {
    let delaySinceLastRotation = Date.now() - this._lastAdRotationTime;
    // Rotate ads immediately if delay since last rotation exceedes config delay
    if (delaySinceLastRotation > this._config.delay * 1000) {
      delaySinceLastRotation = 1;
      this.trigger(delaySinceLastRotation);
    }
  }

  /**
   * trigger ad rotation
   *
   * @param {number} delaySinceLastRotation - Delay since last ad rotation
   * @returns {undefined}
   */
  trigger(delaySinceLastRotation?: number = 0) {
    // Prevent ad rotation when page is inactive
    if (this._config.onlyActivePage && visibilityhelper.isDocHidden()) {
      return;
    }

    if (!this._shouldRotate()) {
      return;
    }

    // Resetting adRotation timer to handle the below case
    // 1. Loaded subset of positions and delayList became empty.
    // 2. Set the adRotation timer.
    // 3. Just completed loading remaining subset of positions and now delayList is empty again.
    // 4. Now clear previous adRotation timer and set it.
    this.clear();

    let delay = this._config.delay * 1000;
    if (isNumber(delaySinceLastRotation) && delaySinceLastRotation > 0) {
      delay = delaySinceLastRotation;
    }

    // we have certain position to be fetched. Let's start timer here.
    // eslint-disable-next-line unicorn/no-this-assignment
    const that = this;
    this._tidRotate = setTimeout(() => that._rotate(), delay);
  }

  /**
   * clear ad rotation
   *
   * @returns {undefined}
   */
  clear() {
    if (this._tidRotate) {
      clearTimeout(this._tidRotate);
      this._tidRotate = undefined;
    }
  }

  /**
   * getter for ad rotation positions
   *
   * @returns {Array<string>} - Ad positions
   */
  getPositions(): Array<string> {
    return this._config.positions;
  }
}

// export singleton object as default for this module
const ar: _adrotation = new _adrotation();
export default ar;
